import React from 'react';
import { data } from 'hudl-base';
import { getConsentState } from '../../utility/consent-utils';
import { subscribeToOneTrustBannerHeight } from '../../utility/onetrust-banner-observer';
import applyTargeting from '../../utility/ads/apply-targeting';
import { STICKY_FOOTER_PLACEMENT } from '../../utility/ads/freestar-placements';
import applyFreestarPrivacy from '../../utility/ads/apply-freestar-privacy';
import './freestar-sticky-footer.scss';

// Triggers Freestar's native on-call sticky footer (hudl-com_sticky_footer,
// configured in FAD). Used on the athlete profile and video pages. Freestar injects
// and manages its own DOM element, so this component renders nothing. It owns the
// lifecycle: apply consent and targeting, call newStickyFooter(), clean up on unmount.
//
// Freestar's native footer handles background color (Google policy), sizing,
// and device targeting from their FAD config.
class FreestarStickyFooter extends React.Component {
    componentDidMount() {
        if (typeof window === 'undefined' || !window.freestar) return;

        this._mounted = true;

        window.freestar.queue = window.freestar.queue || [];
        window.freestar.queue.push(() => {
            if (!this._mounted) return;

            const adData = data.get('ads');
            const backendRequiresNpa = adData?.targetingData?.nonPersonalizedAds === 'True';

            const registerFooter = consentState => {
                if (!this._mounted) return;
                applyFreestarPrivacy(consentState.hasTargeting, backendRequiresNpa);
                // Guard against double-registration on SPA remount — skip if
                // Freestar already has an active sticky footer on this page.
                if (!window.freestar.currentStickyFooter) {
                    // Freestar defines its own GPT slot, so set Hudl's keys page-level before it registers.
                    if (window.googletag?.pubads && adData?.targetingData) {
                        applyTargeting(window.googletag.pubads(), adData.targetingData);
                    }
                    window.freestar.newStickyFooter(STICKY_FOOTER_PLACEMENT);
                    // Marks this instance as the footer's owner (gates teardown). Body padding is
                    // managed in syncFooterAndDecagon, keyed on the footer being present.
                    this._didSetPadding = true;

                    // Freestar's fixed full-width footer paints over the redesign nav's fixed left
                    // sidebar. z-index can't fix it (the nav sorts below a fixed element), so offset
                    // the footer's left edge to the content area. Track it off the shadow
                    // .global-navigation-layout-card, whose left edge changes on collapse/expand and
                    // hydration (the hudl-layout host stays full-viewport and wouldn't fire).
                    const getSidebarLeft = () => {
                        const hudlLayout = document.querySelector('hudl-layout');
                        if (!hudlLayout || !hudlLayout.shadowRoot) return 0;
                        const card = hudlLayout.shadowRoot.querySelector('.global-navigation-layout-card');
                        return card ? card.getBoundingClientRect().left : 0;
                    };
                    const applySidebarOffset = () => {
                        const left = getSidebarLeft();
                        const footer = document.querySelector('#fs-sticky-footer');
                        if (footer) footer.style.left = left > 0 ? `${left}px` : '';
                        const parent = document.querySelector('.fs-sticky-parent');
                        if (parent) parent.style.left = left > 0 ? `${left}px` : '';
                    };
                    if (window.ResizeObserver) {
                        this.navResizeObserver = new window.ResizeObserver(applySidebarOffset);
                    }
                    const observeSidebarCard = () => {
                        if (!this.navResizeObserver || this._navCardObserved) return;
                        const hudlLayout = document.querySelector('hudl-layout');
                        const card = hudlLayout && hudlLayout.shadowRoot
                            ? hudlLayout.shadowRoot.querySelector('.global-navigation-layout-card')
                            : null;
                        if (!card) return;
                        this.navResizeObserver.observe(card);
                        this._navCardObserved = true;
                    };

                    // Footer and shadow card both appear async, and the card lives in a shadow root
                    // body observation can't cross, so poll until both exist. Each tick attaches the
                    // ResizeObserver to the card and applies the offset. Stop when applied, or at 10s.
                    const trySidebarSetup = () => {
                        observeSidebarCard();
                        applySidebarOffset();
                        return getSidebarLeft() > 0 && !!document.querySelector('#fs-sticky-footer');
                    };
                    // Idempotent, and re-run from the body observer: hudl-layout may hydrate after
                    // consent resolves, and a late host must still start the poller.
                    const startSidebarSetup = () => {
                        if (this._sidebarSetupStarted || !document.querySelector('hudl-layout')) return;
                        this._sidebarSetupStarted = true;
                        if (trySidebarSetup()) return;
                        this.navSetupInterval = setInterval(() => {
                            if (trySidebarSetup()) {
                                clearInterval(this.navSetupInterval);
                                clearTimeout(this.navSetupTimeout);
                            }
                        }, 200);
                        // Give up after 10s (matches the Decagon observer).
                        this.navSetupTimeout = setTimeout(() => clearInterval(this.navSetupInterval), 10000);
                    };
                    startSidebarSetup();

                    // Footer and Decagon's chat launcher are both fixed bottom-right. Keep the
                    // launcher above the footer by the footer's real height (a negative overlap puts
                    // its iframe box over the close X and, at Decagon's z-index, eats the tap). On
                    // chat open Decagon fills the screen, so drop the offset and hide the ad. Decagon
                    // exposes no position/z-index/open-close config, so this is DOM-driven.
                    const CHAT_OPEN_MIN_HEIGHT = 200; // launcher is ~100px; the open chat fills the screen
                    const syncFooterAndDecagon = () => {
                        const decagon = document.querySelector('#decagon-iframe');
                        const footer = document.querySelector('#fs-sticky-footer');
                        const parent = document.querySelector('.fs-sticky-parent');
                        const footerPresent = !!footer && footer.offsetHeight > 0
                            && getComputedStyle(footer).display !== 'none';
                        if (footerPresent) this._footerSeen = true;
                        const chatOpen = !!decagon && decagon.offsetHeight > CHAT_OPEN_MIN_HEIGHT;

                        if (chatOpen) {
                            decagon.style.bottom = '0px';
                            if (footer) footer.style.visibility = 'hidden';
                            if (parent) parent.style.visibility = 'hidden';
                            return;
                        }
                        if (footer) footer.style.visibility = '';
                        if (parent) parent.style.visibility = '';
                        // No footer: back to Decagon's own anchor. 0px, not '': Decagon sets the
                        // launcher's bottom inline, so clearing to '' unanchors it to the top.
                        if (decagon) decagon.style.bottom = footerPresent ? `${footer.offsetHeight}px` : '0px';
                        // Reserve the footer's real height while present, release it when gone. Keyed
                        // on footerPresent so nothing is held when the footer never serves.
                        // removeProperty, not '': jsdom won't drop a shorthand longhand on ''.
                        if (footerPresent) document.body.style.paddingBottom = `${footer.offsetHeight}px`;
                        else if (this._footerSeen) document.body.style.removeProperty('padding-bottom');
                    };

                    const attachFooterDecagonObservers = () => {
                        const footer = document.querySelector('#fs-sticky-footer');
                        if (footer && window.ResizeObserver && !this.footerResizeObserver) {
                            this.footerResizeObserver = new window.ResizeObserver(syncFooterAndDecagon);
                            this.footerResizeObserver.observe(footer);
                        }
                        // Watch the footer's parent so we notice Freestar removing the footer
                        // on ad close (a ResizeObserver on a removed node just stops firing).
                        if (footer && footer.parentNode && !this.footerRemovalObserver) {
                            this.footerRemovalObserver = new MutationObserver(syncFooterAndDecagon);
                            this.footerRemovalObserver.observe(footer.parentNode, { childList: true });
                        }
                        const decagon = document.querySelector('#decagon-iframe');
                        if (decagon && window.ResizeObserver && !this.decagonResizeObserver) {
                            this.decagonResizeObserver = new window.ResizeObserver(syncFooterAndDecagon);
                            this.decagonResizeObserver.observe(decagon);
                        }
                    };

                    attachFooterDecagonObservers();
                    syncFooterAndDecagon();
                    // Both elements arrive async, so re-attach and re-sync on DOM changes. Stop
                    // after 10s; the ResizeObservers and removal watch keep running.
                    this.decagonObserver = new MutationObserver(() => {
                        startSidebarSetup();
                        attachFooterDecagonObservers();
                        syncFooterAndDecagon();
                    });
                    this.decagonObserver.observe(document.body, { childList: true, subtree: true });
                    this.decagonObserverTimeout = setTimeout(() => {
                        if (this.decagonObserver) {
                            this.decagonObserver.disconnect();
                            this.decagonObserver = null;
                        }
                    }, 10000);

                    // Lift both Freestar elements above the OneTrust banner when visible:
                    // #fs-sticky-footer carries the background, .fs-sticky-parent the ad.
                    this.unsubscribeBannerObserver = subscribeToOneTrustBannerHeight(height => {
                        const stickyFooter = document.querySelector('#fs-sticky-footer');
                        if (stickyFooter) stickyFooter.style.bottom = `${height}px`;
                        const stickyParent = document.querySelector('.fs-sticky-parent');
                        if (stickyParent) stickyParent.style.bottom = `${height}px`;
                    });
                    this.handleConsentChange = () => {
                        getConsentState().then(({ hasTargeting }) => {
                            window.googletag.cmd.push(() => applyFreestarPrivacy(hasTargeting, backendRequiresNpa));
                        });
                    };
                    window.addEventListener('ConsentScriptsReady', this.handleConsentChange);
                }
            };

            // Privacy settings and targeting need googletag.pubads(), which doesn't exist until
            // gpt.js loads. The freestar queue can drain first, so wait on googletag.cmd rather
            // than skipping them and letting the footer request go out without NPA/RDP.
            getConsentState().then(consentState => {
                if (!this._mounted) return;
                window.googletag = window.googletag || {};
                window.googletag.cmd = window.googletag.cmd || [];
                window.googletag.cmd.push(() => registerFooter(consentState));
            });
        });

    }

    componentWillUnmount() {
        this._mounted = false;

        if (this.decagonObserver) this.decagonObserver.disconnect();
        clearTimeout(this.decagonObserverTimeout);
        if (this.footerResizeObserver) this.footerResizeObserver.disconnect();
        if (this.decagonResizeObserver) this.decagonResizeObserver.disconnect();
        if (this.footerRemovalObserver) this.footerRemovalObserver.disconnect();
        if (this.navResizeObserver) this.navResizeObserver.disconnect();
        clearInterval(this.navSetupInterval);
        clearTimeout(this.navSetupTimeout);
        if (this.unsubscribeBannerObserver) {
            this.unsubscribeBannerObserver();
        }
        if (this.handleConsentChange) {
            window.removeEventListener('ConsentScriptsReady', this.handleConsentChange);
        }

        // Only tear down shared footer state if THIS instance created it: during an SPA
        // remount overlap a non-owning instance must not clear the active footer.
        if (this._didSetPadding) {
            document.body.style.removeProperty('padding-bottom');
            const stickyFooter = document.querySelector('#fs-sticky-footer');
            if (stickyFooter) {
                stickyFooter.style.left = '';
                stickyFooter.style.visibility = '';
            }
            const stickyParent = document.querySelector('.fs-sticky-parent');
            if (stickyParent) {
                stickyParent.style.left = '';
                stickyParent.style.visibility = '';
            }
            const decagon = document.querySelector('#decagon-iframe');
            // 0px, not '': clearing Decagon's inline bottom would unanchor it to the top.
            if (decagon) decagon.style.bottom = '0px';

            if (typeof window !== 'undefined' && window.freestar) {
                window.freestar.queue = window.freestar.queue || [];
                window.freestar.queue.push(() => {
                    // Guard the call: a wrong/missing method silently skips teardown and
                    // double-registers the footer on SPA remount.
                    if (typeof window.freestar.deleteStickyFooter === 'function') {
                        window.freestar.deleteStickyFooter(STICKY_FOOTER_PLACEMENT);
                    }
                    // deleteStickyFooter passes deleteAdSlots an object instead of an id, so it
                    // leaves the footer's GPT slot and Freestar's refresh entry behind. Delete
                    // them by id. Harmless once Freestar fixes it.
                    if (typeof window.freestar.deleteAdSlots === 'function') {
                        window.freestar.deleteAdSlots([STICKY_FOOTER_PLACEMENT]);
                    }
                });
            }
        }
    }

    render() {
        return null;
    }
}

export default FreestarStickyFooter;
